/**
 * Ads (docs/SPEC.md §25.1, §25.6 `POST /v1/site`): the composer's Start, the
 * list, one ad as `GET /v1/ads/{id}` gives it (the same object as the socket's
 * `hello.ad`), its name, Try again. Behind the feature `film`.
 *
 * The ad's page keeps the ad in one query (`adKey(id)`); the socket
 * (`ad/useAdSocket.ts`) and every write of the brief (`api/brief.ts`) put what
 * they learn into it, so the page renders from one object.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type PricedAnswer, api, needsConfirm } from "./client";
import { ME_KEY } from "./me";

// ------------------------------------------------------------------ types

/** §6.1 (P28-T06): `brief → building → ready`, `failed → building`. */
export type AdState = "brief" | "building" | "ready" | "failed";

export const AD_LENGTHS = [6, 10, 15, 20, 30] as const;
export type AdLength = (typeof AD_LENGTHS)[number];
export const AD_ASPECTS = ["9:16", "1:1", "16:9"] as const;
export type AdAspect = (typeof AD_ASPECTS)[number];

export interface AdOptions {
  length_s: AdLength;
  aspect: AdAspect;
}

/** §4.1's defaults: 15 s, 9:16. */
export const DEFAULT_AD_OPTIONS: AdOptions = { length_s: 15, aspect: "9:16" };

/** The five optional fields of Details (§4.1), by the keys the brief model
 *  reads (`backend/brief/context.py::DETAIL_FIELDS`). */
export const DETAIL_KEYS = [
  "subject",
  "what",
  "audience",
  "better",
  "call",
] as const;
export type DetailKey = (typeof DETAIL_KEYS)[number];
export type AdDetails = Partial<Record<DetailKey, string>>;

/** Where a line of the brief came from (§5.4). A data entry's may also be
 *  `sheet:<id>!<cell>`. */
export type Provenance =
  | "said"
  | "site"
  | "sheet"
  | "photo"
  | "reference"
  | "brand"
  | "template"
  | "decided";

export interface BriefText {
  text: string;
  from: Provenance;
}

export interface BriefScene {
  id: string;
  name: string;
  seconds: number;
  what: string;
  stage: string;
  lighting: string;
  camera: string;
  objects: string[];
  captions: string[];
  from: Provenance;
}

export interface BriefCast {
  id: string;
  what: string;
  source: string;
  from: Provenance;
}

/** A suggestion of the brief (§5.5): `caption` words to read on screen,
 *  accepted as a caption; `staging` what to show or how to film it, accepted
 *  into its scene's `what`. What only the person can give is asked where it
 *  is given (a cast panel row, the card's Ends with line), never suggested. */
export interface BriefSuggestion {
  text: string;
  kind: "caption" | "staging";
  scene: string | null;
  /** A value only the person can give that the suggestion asks for: Accept
   *  opens a field for it, and the value typed becomes the caption. */
  needs?: SuggestionNeeds | null;
}

/** What a suggestion may ask for (`brief.schema.json`'s `needs`). */
export type SuggestionNeeds =
  "price" | "discount" | "phone" | "address" | "hours" | "date";

export type Music = "calm" | "energetic" | "none" | "decide";
export type VoiceCharacter = "warm" | "clear" | "none";

/** `brief.json` (§5.4) as the page reads it. */
export interface Brief {
  subject: BriefText;
  promise: BriefText;
  audience: BriefText;
  mood: { words: string[]; from: Provenance; reference: string | null };
  length_s: number;
  aspect: string;
  look: {
    brand_kit_id: string | null;
    palette: string[];
    fonts: string[];
    from: Provenance;
  };
  end_card: {
    /** null: nobody gave a name, the end card shows none. */
    name: string | null;
    url: string | null;
    tagline: string | null;
    from: Provenance;
  };
  beats: { name: string; t0: number; t1: number }[];
  scenes: BriefScene[];
  cast: BriefCast[];
  screens: { object: string; shows: string }[];
  sound: {
    music: Music;
    sfx: boolean;
    voice: { language: string; character: VoiceCharacter };
    from: Provenance;
  };
  data: { id: string; value: string; unit: string | null; from: string }[];
  suggestions: BriefSuggestion[];
  questions_asked: number;
  template_id: string | null;
  reference_notes: string | null;
}

/** The fields a question may be about, and the card's editable lines
 *  (`backend/ads/brief_card.py::EDITABLE`). */
export type BriefField =
  | "subject"
  | "promise"
  | "audience"
  | "mood"
  | "length_s"
  | "aspect"
  | "end_card"
  | "scenes"
  | "cast"
  | "sound";

export interface BriefQuestion {
  text: string;
  choices: string[];
  selected: number;
  field: string;
}

export interface BriefTurn {
  role: "user" | "model";
  text: string;
  at: string | null;
  question?: BriefQuestion;
  /** A user turn made on the card: an edit, an accepted suggestion. */
  kind?: string;
}

/** The conversation as the page shows it (`service.brief_view`). */
export interface BriefState {
  turns: BriefTurn[];
  question: BriefQuestion | null;
  brief: Brief | null;
  final: boolean;
  questions_asked: number;
  title: string | null;
  error_code: string | null;
  sentence: string | null;
  working: boolean;
}

export interface AdVersion {
  id: string;
  n: number;
  kind: string;
  state: string;
  say: string | null;
  error_code: string | null;
  sentence: string | null;
  is_current: boolean;
  scenes: { id: string; name: string; seconds: number }[];
  created_at: string | null;
  ready_at: string | null;
  /** Where the player reads the version (P22-T07), relative to the API:
   *  while it builds too, answering the scenes built so far (P28-T06). */
  manifest_url?: string | null;
}

export interface AdSite {
  url: string | null;
  error: string | null;
  title: string | null;
  description: string | null;
  colours: string[];
  fonts: string[];
  sentences: string[];
  logo: boolean;
  pictures: number;
}

export interface AdDetail {
  id: string;
  title: string;
  title_source: string;
  state: AdState;
  text: string;
  details: AdDetails;
  url: string | null;
  site: AdSite | null;
  options: Partial<AdOptions>;
  brand_kit_id: string | null;
  brand_kit_name: string | null;
  template_id: string | null;
  uploads: { id: string; role: string }[];
  sheets: {
    upload_id: string;
    name: string;
    columns: string[];
    rows: number;
  }[];
  brief: Brief | null;
  brief_state: BriefState;
  questions_asked: number;
  current_version: AdVersion | null;
  building_version: AdVersion | null;
  focus_scene: string | null;
  outputs: unknown[];
  error_code: string | null;
  sentence: string | null;
  credits_estimated: number | null;
  credits_captured: number | null;
  created_at: string;
  updated_at: string | null;
}

export interface AdSummary {
  id: string;
  title: string;
  state: AdState;
  /** The current version's manifest while it builds or plays, else null:
   *  the list frames the first scene from it in the browser (P28-T10). */
  manifest_url: string | null;
  length_s: number | null;
  aspect: string | null;
  created_at: string;
  credits_captured: number | null;
}

export interface CreateAdBody {
  text: string;
  details: AdDetails;
  url?: string;
  uploads: string[];
  reference: string[];
  options: AdOptions;
  /** Absent: the organisation's default kit; `null`: none (§4.1). */
  brand_kit_id?: string | null;
  template_id?: string;
  /** Objects chosen for the cast before the brief: `objects:<id>` (§23). */
  cast?: string[];
  /** The attached photo that is the person's own logo (one of `uploads`): the
   *  film's logo before the brand kit's and the site's. */
  logo?: string;
  /** What the person left out of the site's findings: pictures by their place
   *  in `POST /v1/site`'s answer (1 is the first), and the sentences. */
  site_excluded?: SiteExcluded;
}

/** The site's findings the person took out in the composer. */
export interface SiteExcluded {
  pictures: number[];
  sentences: boolean;
}

/** `POST /v1/site`'s answer (§25.6): the logo and pictures as short-lived
 *  links. */
export interface SiteFindings {
  state?: "ready";
  url: string;
  title: string | null;
  description: string | null;
  logo: { url: string; media_type: string } | null;
  colours: string[];
  fonts: string[];
  pictures: { url: string; media_type: string }[];
  sentences: string[];
  provenance: string;
}

// ------------------------------------------------------------------ hooks

export const ADS_KEY = ["ads"] as const;
export const adKey = (id: string) => ["ad", id] as const;
export const TITLE_MAX = 80;

const path = (id: string) => `/v1/ads/${encodeURIComponent(id)}`;

export function useAds() {
  return useQuery({
    queryKey: ADS_KEY,
    queryFn: () => api<{ items: AdSummary[] }>("/v1/ads"),
  });
}

/** One ad. `pollMs` refetches while the socket is not open. */
export function useAd(id: string, pollMs: number | false = false) {
  return useQuery({
    queryKey: adKey(id),
    queryFn: () => api<AdDetail>(path(id)),
    refetchInterval: pollMs,
  });
}

/** `POST /v1/ads` (201): **Start**. The brief's first turn is metered
 *  (§15.1): at or above `PRICE_CONFIRM_CREDITS` the route answers 202
 *  `{estimate, kind, confirm: "required"}` and makes no ad until it is sent
 *  again with `confirm: true`; below the estimate, 402. */
export function useCreateAd() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      idempotencyKey,
      ...body
    }: CreateAdBody & { idempotencyKey?: string; confirm?: boolean }) =>
      api<AdDetail | PricedAnswer>("/v1/ads", {
        method: "POST",
        body,
        idempotencyKey,
      }),
    onSuccess: (ad) => {
      if (needsConfirm(ad)) return;
      queryClient.setQueryData(adKey((ad as AdDetail).id), ad);
      void queryClient.invalidateQueries({ queryKey: ADS_KEY });
      void queryClient.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

/** `PATCH /v1/ads/{id} {title}`: the user's name, never replaced by the
 *  model's (§14.1.1). */
export function useRenameAd(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (title: string) =>
      api<AdDetail>(path(id), { method: "PATCH", body: { title } }),
    onSuccess: (ad) => {
      queryClient.setQueryData(adKey(id), ad);
      void queryClient.invalidateQueries({ queryKey: ADS_KEY });
    },
  });
}

/** `POST /v1/ads/{id}/retry` (202), **Try again** (§6.2): only in
 *  `failed`; the film baked again (free), or written again when it never was
 *  (a turn: its estimate first, §15.1 — 402 below it, 202 `{estimate,
 *  confirm: "required"}` at or above `PRICE_CONFIRM_CREDITS` until sent
 *  with `confirm`); 409 `invalid_state` otherwise. */
export function useRetry(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      idempotencyKey,
      confirm = false,
    }: {
      idempotencyKey: string;
      confirm?: boolean;
    }) =>
      api<AdDetail | PricedAnswer>(`${path(id)}/retry`, {
        method: "POST",
        idempotencyKey,
        ...(confirm ? { body: { confirm: true } } : {}),
      }),
    onSuccess: (ad) => {
      if (needsConfirm(ad)) return;
      queryClient.setQueryData(adKey(id), ad);
      void queryClient.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

/** `POST /v1/site` while the worker reads the site (202): the composer asks
 *  `GET /v1/site/{id}` until it is the findings. */
export interface SiteReading {
  state: "reading";
  id: string;
  url: string;
}

/** How often, and for how long at most, a site being read is asked about. */
export const SITE_POLL_MS = 750;
export const SITE_WAIT_MS = 90_000;

const pause = (ms: number) => new Promise<void>((done) => setTimeout(done, ms));

function isReading(x: SiteFindings | SiteReading): x is SiteReading {
  return x.state === "reading";
}

/** `POST /v1/site {url}`: what a site says about itself, for the chips. A
 *  site the hour's cache does not hold is read by the worker: the answer is
 *  then `{state: "reading", id}` and `GET /v1/site/{id}` is asked until it is
 *  the findings or an error (`url_unreachable`), at most `waitMs`. */
export async function readSite(
  url: string,
  pollMs: number = SITE_POLL_MS,
  waitMs: number = SITE_WAIT_MS,
): Promise<SiteFindings> {
  let found = await api<SiteFindings | SiteReading>("/v1/site", {
    method: "POST",
    body: { url },
  });
  const until = Date.now() + waitMs;
  while (isReading(found)) {
    if (Date.now() > until) throw new Error("site_read_timeout");
    await pause(pollMs);
    found = await api<SiteFindings | SiteReading>(
      `/v1/site/${encodeURIComponent(found.id)}`,
    );
  }
  return found;
}

export function useReadSite() {
  return useMutation({ mutationFn: (url: string) => readSite(url) });
}
